import React from 'react'
import { useTheme } from '../../context/ThemeContext'
import type { PublicPlanFeatures } from '../../services/plans'
import { formatAccessDuration } from '../../helper-functions/planTier'

type SubscriptionPlan = {
  name?: string
  billingInterval?: 'month' | 'year'
  billingMode?: 'SUBSCRIPTION' | 'ONE_TIME'
  // ONE_TIME plans only: null/0 = lifetime, 12 = 12 months of access.
  accessDurationMonths?: number | null
  features?: PublicPlanFeatures
}

type Props = {
  className?: string
  plan?: SubscriptionPlan
  // 'grid' (default): responsive 4-up cards. 'list': compact stacked rows for
  // narrow containers (e.g. the signup/invitation column). 'inline': a wrapping
  // row of label:value chips — minimal vertical space, no card chrome.
  variant?: 'grid' | 'list' | 'inline'
}

// Keys defined in the backend feature catalog. Unknown keys that a plan row
// may carry are ignored so the UI doesn't render raw identifiers.
const FEATURE_META: Record<
  string,
  { label: string; kind: 'limit' | 'boolean'; unit?: string }
> = {
  'boards.limit': { label: 'Boards', kind: 'limit' },
  'licenses.limit': { label: 'Licenses', kind: 'limit' },
  'guests.limit': { label: 'Guest invites', kind: 'limit' },
  'board.people.limit': { label: 'Members per board', kind: 'limit' },
  'ai.tokens.limit': { label: 'AI tokens', kind: 'limit', unit: 'per month' },
  'integrations.docusign': { label: 'DocuSign integration', kind: 'boolean' },
}

const FEATURE_ORDER = [
  'boards.limit',
  'licenses.limit',
  'guests.limit',
  'board.people.limit',
  'ai.tokens.limit',
  'integrations.docusign',
]

const formatValue = (
  key: string,
  value: number | boolean | null | undefined,
): { text: string; tone: 'on' | 'off' | 'neutral' } => {
  const meta = FEATURE_META[key]
  if (meta?.kind === 'boolean') {
    return value === true
      ? { text: 'Included', tone: 'on' }
      : { text: 'Not included', tone: 'off' }
  }
  // limit
  if (value === null || value === undefined) {
    return { text: 'Unlimited', tone: 'on' }
  }
  const unit = meta?.unit ? ` ${meta.unit}` : ''
  return { text: `${value}${unit}`, tone: 'neutral' }
}

const toneToClass = (tone: 'on' | 'off' | 'neutral') =>
  tone === 'on'
    ? 'badge-light-success'
    : tone === 'off'
    ? 'badge-light-danger'
    : 'badge-light-primary'

export const PlanFeaturesCard: React.FC<Props> = ({ className = '', plan, variant = 'grid' }) => {
  const { theme } = useTheme()
  const features = plan?.features ?? {}
  const knownEntries = FEATURE_ORDER.filter((k) => k in features).map((k) => [
    k,
    (features as Record<string, number | boolean | null | undefined>)[k],
  ] as const)

  if (!plan || knownEntries.length === 0) {
    return null
  }

  // Compact, chrome-less chips — keeps the section short when shown inline
  // alongside other controls (e.g. the change-plan list).
  if (variant === 'inline') {
    return (
      <div className={className}>
        <div className='d-flex flex-wrap gap-2'>
          {knownEntries.map(([key, value]) => {
            const meta = FEATURE_META[key]
            const { text, tone } = formatValue(key, value)
            return (
              <span key={key} className={`badge ${toneToClass(tone)} fs-7 fw-semibold`}>
                {meta.label}: {text}
              </span>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className={`card ${className}`}>
      <div className={`card-header border-0 pt-5 ${theme === 'dark' ? 'theme__div' : ''}`}>
        <h3 className='card-title align-items-start flex-column'>
          <span className={`fw-bolder fs-3 mb-1 ${theme === 'dark' ? 'white__text' : ''}`}>
            Your Plan Features
          </span>
          {plan.name && (
            <span className='text-muted fw-semibold fs-7'>
              {plan.name}
              {plan.billingMode === 'ONE_TIME'
                ? ` - one-time · ${formatAccessDuration(plan.accessDurationMonths)}`
                : plan.billingInterval
                ? ` - billed ${plan.billingInterval}ly`
                : ''}
            </span>
          )}
        </h3>
      </div>
      <div className={`card-body py-5 ${theme === 'dark' ? 'theme__div' : ''}`}>
        {variant === 'list' ? (
          <div className='d-flex flex-column'>
            {knownEntries.map(([key, value], idx) => {
              const meta = FEATURE_META[key]
              const { text, tone } = formatValue(key, value)
              return (
                <div
                  key={key}
                  className={`d-flex justify-content-between align-items-center py-3 ${
                    idx < knownEntries.length - 1
                      ? theme === 'dark'
                        ? 'border-bottom border-gray-700'
                        : 'border-bottom border-gray-200'
                      : ''
                  }`}
                >
                  <span
                    className={`fw-semibold fs-6 ${theme === 'dark' ? 'text-gray-400' : 'text-muted'}`}
                  >
                    {meta.label}
                  </span>
                  <span className={`badge ${toneToClass(tone)} fs-7 fw-bold`}>{text}</span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className='row g-4'>
            {knownEntries.map(([key, value]) => {
              const meta = FEATURE_META[key]
              const { text, tone } = formatValue(key, value)
              return (
                <div key={key} className='col-12 col-md-6 col-xl-3'>
                  <div
                    className={`p-4 rounded border ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}
                  >
                    <div
                      className={`fw-semibold fs-7 mb-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-muted'
                      }`}
                    >
                      {meta.label}
                    </div>
                    <span className={`badge ${toneToClass(tone)} fs-6 fw-bold`}>{text}</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
